declare global {
  interface Window {
    mediafly: {
      render: {
        [name: string]: (
          containerId: string,
          options?: Record<string, unknown>
        ) => Promise<void | MFERoot>;
      };
      unmount: {[name: string]: (container: string | MFERoot) => Promise<void>};
    };
  }

  // The 'Root' type from 'react-dom/client'
  interface MFERoot {
    render: (children: React.ReactNode) => void;
    unmount: () => void;
  }
}

export const fetchedManifests: Map<string, {[key: string]: string}> = new Map();

const prodHost = 'users.execvision.io';
const stagingHost = 'users.staging.execvision.io';

/**
 * Returns domain based on whether we're running in local, staging or prod
 */
export function getDomainFor(name: string): string {
  const hostname = window.location.hostname;
  if (prodHost === hostname) return `https://mfe.execvision.io/${name}`;
  if (stagingHost === hostname) return `https://mfe.staging.execvision.io/${name}`;
  return 'http://localhost:8081';
}

/**
 * Renders an MFE app by:
 *
 * 1. fetching its manifest
 * 2. looping through CSS and JS entries in the manifest
 * 3. creating script & style tags and inserting them into the DOM
 * 4. this causes the browser to request those files itself
 * 5. when all files are loaded, calling the MFE's render method on window
 */
export async function renderMicroFrontend(
  name: string,
  options: Record<string, unknown> = {},
  containerId = 'container'
): Promise<void | MFERoot> {
  const domain = getDomainFor(name);

  // We don't want to cache the asset manifest under any circumstances.
  // For example, an MFE prod deploy could happen while the user navigates around
  // in Viewer and in that case we want to make sure we immediately request
  // the most up-to-date versions of the MFE assets when we navigate to it.
  const manifest = await window
    .fetch(`${domain}/asset-manifest.json?${Date.now()}`)
    .then(response => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // This typing sucks, we should be able to get a lib typing where response.json is a generic
      const data = response.json() as unknown as Record<string, string>;

      if (!data) {
        throw new Error('No data from asset manifest response.');
      }

      return data;
    });

  const promises = Object.values(manifest)
    // We don't want to load any assets that are already on the page.
    .filter(filename => !document.getElementById(`mfe-${name}-${filename}`))

    // For "new" assets, create a promise that will fetch the asset and create a tag.
    .map(
      filename =>
        new Promise((resolve, reject) => {
          let el: HTMLScriptElement | HTMLLinkElement;

          if (filename.endsWith('.css')) {
            el = createLinkElement(`${domain}/${filename}`);
          } else if (filename.endsWith('.js')) {
            el = createScriptElement(`${domain}/${filename}`);
          } else {
            throw new Error("Manifest contained a file that isn't CSS or JS.");
          }

          el.id = `mfe-${name}-${filename}`;
          el.onload = resolve;
          el.onerror = reject;

          document.head.appendChild(el);
        })
    );

  return Promise.all(promises).then(render);

  function render() {
    // tslint:disable-next-line no-console
    console.info('Rendering MFE into:', document.getElementById(`${name}-${containerId}`));

    return window.mediafly.render[name](`${name}-${containerId}`, options);
  }
}

/**
 * Unmounts an MFE app from the DOM, returning a promise which resolves when
 * the unmount is completed or aborted.
 */
export function unmountMicroFrontend(name: string, containerId = 'container'): Promise<void> {
  const targetId = `${name}-${containerId}`;
  const el = document.getElementById(targetId);
  if (!el) {
    // tslint:disable-next-line no-console
    console.info('Unmounting MFE. No element found with ID:', targetId);
    return Promise.resolve();
  }

  try {
    // tslint:disable-next-line no-console
    console.info('Unmounting MFE at element:', el);

    return window.mediafly.unmount[name](targetId);
  } catch (error) {
    return Promise.resolve();
  }
}

/**
 * Util function to create a link element
 */
function createLinkElement(href: string): HTMLLinkElement {
  const el = document.createElement('link');
  el.rel = 'stylesheet';
  el.href = href;
  return el;
}

/**
 * Util function to create a script element
 */
function createScriptElement(src: string): HTMLScriptElement {
  const el = document.createElement('script');
  el.src = src;
  return el;
}
